import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { DonutGraph } from '@flexo/atoms';
import styles from './community-stats.module.scss';
import { CommunityStatsSkeleton } from './community-stats-skeleton';
import { Shimmer } from '@flexo/atoms';

import { useSelector } from 'react-redux';
import { Haptics } from '@capacitor/haptics';
import { formatToMaxFourDigits } from '@flexo/general';
import { useKpiData } from '@flexo/hooks';

export interface CommunityStatsData {
  totalAmount: string;
  totalAmounttype: string;
  colors: string[];
  keys: string[];
  title: string;
  percentage: number;
}
export interface CommunityStatsProps {
  from_network: CommunityStatsData;
  to_network: CommunityStatsData;
  shared: CommunityStatsData;
  dataInTimeSpan: boolean;
}
export const communityPlaceholderData = [{ label: '', value: 100 }];

export function CommunityStats(props) {
  const kpi = useSelector((state: any) => state.kpi);

  const { CommunityStats__Stat } = styles;
  const [selected, setSelected] = useState<string | null>(null);
  const ThemeStore = useSelector((state: any) => state.theme);

  const holdTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useTranslation();
  

  const LOADING_STATE = kpi?.EnergyKpiDataCommunityAsyncStatus;
  
  const comparisonKpiApiData =
    kpi?.ComparisonCommunity?.['comparison']?.[0]?.kpi || ([] as any);
  
  // Use the new hook for KPI data access
  const { getCardValue: getKpiCardValue } = useKpiData(comparisonKpiApiData, 'energy');
  
  // Replace the current loading state check with the consistent pattern
  if (LOADING_STATE === 'PENDING' && (!comparisonKpiApiData || comparisonKpiApiData.length === 0)) {
    return <CommunityStatsSkeleton 
      props={props.data} 
      count={props.data?.length || 3}
      gradientBorder={props.gradientBorder}
      dataInTimeSpan={props.dataInTimeSpan}
    />;
  }

  const getCardValue = (key) => {
    const cardValue = getKpiCardValue(key);
    if (cardValue?.value === 0 || !cardValue?.value) {
      return {
        value: '-.-',
        unit: '',
      };
    }
    return cardValue;
  };
  const getTotalFormattedValue = (key) => {
    return getKpiCardValue(key);
  };

  const formatGraphData = (item) => {
    const result = item.map((el) => {
      return {
        id: el?.id,
        label: el?.label,
        value: getTotalFormattedValue(el?.value)?.value,
        formattedValue: getTotalFormattedValue(el?.formattedValue)?.value,
      };
    });
    return result;
  };

  // const getColors = (graphDataForColors) => {
  //   return graphDataForColors.map((el) => {
  //     const baseColorPart = ThemeStore.colors[el.color];
  //     if (baseColorPart) {
  //       // Assuming el.colorShade is the part to append to complete the color string
  //       return baseColorPart + (el.colorShade || ''); // Add el.colorShade, or empty string if it's null/undefined
  //     }
  //     // Fallback if the base color part isn't found in the theme
  //     return '#CCCCCC';
  //   });
  // };
  const getColors = (item) => {
    const color = item.map((el) => {
      return ThemeStore.colors[el.color] + el.colorShade;
    });
    return color;
  };

  const getPercentageValue = (item) => {
    const percentage = comparisonKpiApiData?.find(
      (el) => el.name === item.percentage
    )?.value;
    if (percentage === 0 || !percentage) {
      return '-.-';
    }
    return percentage;
  };

  const startHold = (valueLabel) => {
    if (selected !== null) return;
    
    // Check if community value is 0 in the formatted graph data
    const communityItem = formatGraphData(valueLabel.graphData).find(item => item.label === 'community');
    if (communityItem && communityItem.value === 0) return;
    
    setSelected(valueLabel.title);
    Haptics.vibrate({ duration: 10 });
    // Set a timeout for the desired duration (e.g., 2 seconds)

    holdTimeout.current = setTimeout(() => {
      // Do nothing if held for the full duration (or perform another action if needed)
    }, 2000);
  };

  const endHold = () => {
    clearTimeout(holdTimeout.current as any); // clear the timeout if user releases before the duration
    setSelected(null);
  };


  return (
    <div
      className={`${styles.CommunityStats} ${
        props.gradientBorder && styles.CommunityStats__GradientBorder
      }`}
      style={{ position: 'relative' }}
    >
      {LOADING_STATE === 'PENDING' && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%',
          height: '100%',
          zIndex: 999999,
          overflow: 'hidden'
        }}>
          <Shimmer />
        </div>
      )}
      <div className={styles.CommunityStats}>
        {props.data.map((item, index) => {
          const formattedGraphItems = formatGraphData(item.graphData);
          const donutColors = getColors(item.graphData);
          const donutKeys = formattedGraphItems.map((d) => d.label);

          return (
            <div
              className={CommunityStats__Stat}
              key={item.title}
              onTouchStart={() => startHold(item)}
              onTouchEnd={endHold}
            >
              <DonutGraph
                width={105}
                height={105}
                graphData={formattedGraphItems}
                colors={donutColors}
                keys={donutKeys}
                totalAmount={formatToMaxFourDigits(getCardValue(item.totalAmount)?.value)}
                totalAmounttype={getCardValue(item.totalAmount)?.unit}
                selectedId={
                  selected === item.title ? item.graphData[0]?.id : null
                }
                setSelectedId={(id) => setSelected(id ? item.title : null)}
                animate={false}
                dataInTimeSpan={true}
                isCommunityStats={true}
              />
              <div
                className={styles.CommunityStats__Stat__Information}
                onTouchStart={() => startHold(item)}
                onTouchEnd={endHold}
              >
                <div
                  className={`heading5 ${styles.CommunityStats__Stat__Title}`}
                >
                  {t(`widgets.communityStats.${item.title}`)}
                </div>
                <div
                  className={`detail ${styles.CommunityStats__Stat__Description}`}
                >
                  {t(`widgets.communityStats.me`, {
                    percentage: getPercentageValue(item),
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CommunityStats;
